"use client"

import { useState, useMemo } from "react";
import { ChevronLeftIcon, CheckIcon, SearchIcon } from "lucide-react";
import { useQuotesWatchlistStore } from "@/lib/quotes-watchlist/store";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client";
import {
  buildWatchlistQuoteDisplay,
  formatWatchlistPrice,
  formatWatchlistPercent,
} from "@/components/quotes-view/watchlist/watchlistQuoteUtils";
import { getSymbolDescription } from "@/components/quotes-view/lib/symbolDescriptions";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { cn } from "@/lib/utils";

interface SymbolSelectionScreenProps {
  selectedSymbol: string;
  onSelectSymbol: (symbol: string) => void;
  onBack: () => void;
}

interface SymbolPickerRowProps {
  symbol: string;
  isSelected: boolean;
  onSelect: (symbol: string) => void;
}

/**
 * Per-symbol row that subscribes to its own store slice so a quote tick
 * re-renders only this row, not the whole list.
 */
function SymbolPickerRow({ symbol, isSelected, onSelect }: SymbolPickerRowProps) {
  const entry = useSubscribedSymbolsStore((state) =>
    state.byCurrency.get(symbol),
  );

  const description = getSymbolDescription(symbol, entry?.symbol.description);
  const quote = entry?.quote ? buildWatchlistQuoteDisplay(entry) : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(symbol)}
      className={cn(
        "w-full h-10 flex items-center gap-2 px-3 text-left transition-colors hover:bg-muted/60 border-b border-border/60 last:border-b-0",
        isSelected && "bg-muted",
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold leading-tight text-foreground">
          {symbol}
        </div>
        <div className="text-[10.5px] text-muted-foreground truncate leading-tight">
          {description}
        </div>
      </div>

      {quote ? (
        <>
          <span
            className={cn(
              "font-mono text-[10.5px] tabular-nums",
              quote.dayPercent >= 0 ? "text-blue-400" : "text-red-400",
            )}
          >
            {formatWatchlistPercent(quote.dayPercent)}
          </span>
          <span className="flex flex-col items-end font-mono text-[10.5px] tabular-nums leading-tight">
            <span className="text-blue-400">
              {formatWatchlistPrice(quote.bid, symbol)}
            </span>
            <span className="text-red-400">
              {formatWatchlistPrice(quote.ask, symbol)}
            </span>
          </span>
        </>
      ) : (
        <span className="font-mono text-[10.5px] text-muted-foreground">—</span>
      )}

      {isSelected && (
        <CheckIcon className="h-4 w-4 shrink-0 text-foreground" />
      )}
    </button>
  );
}

export default function SymbolSelectionScreen({
  selectedSymbol,
  onSelectSymbol,
  onBack,
}: SymbolSelectionScreenProps) {
  const watchlist = useQuotesWatchlistStore((state) => state.watchlist);
  const symbols = useMemo(
    () => watchlist.map((item) => item.symbolKey),
    [watchlist],
  );

  const [searchTerm, setSearchTerm] = useState("");

  // Filter over ticker AND resolved description (case-insensitive).
  const filteredSymbols = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return symbols;
    const byCurrency = useSubscribedSymbolsStore.getState().byCurrency;
    return symbols.filter((symbol) => {
      if (symbol.toLowerCase().includes(term)) return true;
      const description = getSymbolDescription(
        symbol,
        byCurrency.get(symbol)?.symbol.description,
      );
      return description.toLowerCase().includes(term);
    });
  }, [symbols, searchTerm]);

  const handleSelect = (symbol: string) => {
    onSelectSymbol(symbol);
    onBack();
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header: back · title · count chip */}
      <div className="flex items-center gap-1 px-2 h-11 bg-background border-b border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="p-2 hover:bg-muted"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <h1 className="text-sm font-medium text-foreground">Select Symbol</h1>
        <span className="ml-auto mr-1 font-mono text-[10px] text-muted-foreground bg-muted rounded-full px-2 py-0.5">
          {symbols.length} symbols
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-3 min-h-0">
        {/* Search */}
        <div className="relative mb-3 flex-shrink-0">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search ticker or description…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 pl-8 text-[13px] border-border bg-muted/30"
          />
        </div>

        {/* Symbol list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {symbols.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-1">
                No symbols in watchlist
              </p>
              <p className="text-[11px] text-muted-foreground">
                Add symbols in the Quotes tab to see them here
              </p>
            </div>
          ) : filteredSymbols.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-1">
                No symbols found
              </p>
              <p className="text-[11px] text-muted-foreground">
                Try adjusting your search
              </p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              {filteredSymbols.map((symbol) => (
                <SymbolPickerRow
                  key={symbol}
                  symbol={symbol}
                  isSelected={selectedSymbol === symbol}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
